// Pure cents-only helpers reused by createManualInvoice / updateInvoice /
// generateNextInvoice in the mock dealsApi. Mirrors the prototype's derived
// field logic so behaviour matches when the real backend swap lands. Keep this
// file free of localStorage / network access.

import type { Invoice, InvoiceItem, InvoiceStatus } from '../types/invoice'
import { computeLineTotalCents, computeSubtotalCents } from '../utils/invoiceMath'

export { computeLineTotalCents, computeSubtotalCents }

export function invoiceTotalCents(invoice: Invoice): number {
  return invoice.totalCents ?? 0
}

export function invoicePaidCents(invoice: Invoice): number {
  if (typeof invoice.paidAmountCents === 'number') return invoice.paidAmountCents
  if (invoice.status === 'Paid') return invoiceTotalCents(invoice)
  return 0
}

interface CarryCredit {
  carryCents: number
  creditCents: number
}

export function computeCarryCreditCents(prev: Invoice | null | undefined): CarryCredit {
  if (!prev) return { carryCents: 0, creditCents: 0 }
  const prevTotal = invoiceTotalCents(prev)
  const prevPaid = invoicePaidCents(prev)
  if (prev.status === 'Partially Paid') {
    const carryCents =
      typeof prev.outstandingCents === 'number'
        ? prev.outstandingCents
        : Math.max(0, prevTotal - prevPaid)
    return { carryCents, creditCents: 0 }
  }
  return { carryCents: 0, creditCents: Math.max(0, prevPaid - prevTotal) }
}

/**
 * Recalculates derived cents fields (`subtotalCents`, `totalCents`,
 * `paidAmountCents`, `outstandingCents`) and — when `inferStatus` is true —
 * auto-transitions `status` from paid amount. Locked statuses (Voided /
 * Scheduled / Draft) are always preserved verbatim.
 */
export function recalculateInvoiceState(
  invoice: Invoice,
  prev: Invoice | null | undefined,
  options: { inferStatus?: boolean } = {},
): Invoice {
  const items: InvoiceItem[] = (invoice.items ?? []).map((item) => ({
    ...item,
    amountCents: computeLineTotalCents(item),
  }))
  const subtotalCents =
    items.length > 0 ? computeSubtotalCents(items) : invoice.subtotalCents ?? 0
  const { carryCents, creditCents } = computeCarryCreditCents(prev)
  const carry =
    typeof invoice.prevBalanceOverrideCents === 'number'
      ? Math.max(0, invoice.prevBalanceOverrideCents)
      : carryCents

  const totalCents = Math.max(0, subtotalCents + carry - creditCents)
  const paidAmountCents = invoicePaidCents(invoice)
  const outstandingCents = Math.max(0, totalCents - paidAmountCents)

  const isStatusLocked =
    invoice.status === 'Voided' || invoice.status === 'Scheduled' || invoice.status === 'Draft'
  const inferStatus = !!options.inferStatus && !isStatusLocked

  let status: InvoiceStatus = invoice.status
  if (inferStatus) {
    if (paidAmountCents === 0) status = 'Unpaid'
    else if (paidAmountCents >= totalCents) status = 'Paid'
    else status = 'Partially Paid'
  }

  return {
    ...invoice,
    items,
    subtotalCents,
    totalCents,
    paidAmountCents,
    outstandingCents,
    status,
  }
}

/** Used by createManualInvoice + AddInvoiceForm so both stay in sync. */
export function nextInvoiceNumber(args: {
  order: string
  nextInvoiceSeq: number | undefined
  invoiceCount: number
}): string {
  const seq = args.nextInvoiceSeq ?? args.invoiceCount + 1
  const date = new Date()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = String(date.getFullYear()).slice(2)
  // Re-use the deal's order suffix (e.g. "JD-1") so invoices share the
  // grouping prefix mirrored in the prototype.
  const orderSuffix = args.order.replace(/^D#\d+-/, '')
  return `I#${month}${day}${year}-${orderSuffix}-${seq}`
}

export function todayMDY(): string {
  const date = new Date()
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`
}
